import { useState } from 'react'
import { Link } from 'react-router-dom'

import { Button, PageHeader } from '@/components/layout/ui'
import { ErrorBanner, Spinner, cn } from '@/features/issue'
import { useWorkspace } from '@/lib/workspace'

import { NotificationRow } from './NotificationRow'
import { useNotificationFeed, useNotificationMutations, useUnreadNotificationCount } from './hooks'

/** `/{slug}/notifications` — the whole feed, with paging and a filter. */
export default function NotificationsPage() {
  const workspace = useWorkspace()
  const [unreadOnly, setUnreadOnly] = useState(false)
  const unread = useUnreadNotificationCount(workspace.slug)
  const feed = useNotificationFeed(workspace.slug, unreadOnly)
  const { markRead, markUnread, markAllRead } = useNotificationMutations(workspace.slug)
  const items = feed.data?.pages.flatMap((page) => page.data) ?? []
  const count = unread.data ?? 0

  return (
    <div className="mx-auto max-w-3xl px-6 py-10">
      <PageHeader
        title="Notifications"
        description={count > 0 ? `${count} unread` : 'You are all caught up.'}
        action={
          <div className="flex items-center gap-2">
            <div className="flex rounded-lg border border-ink-200 p-0.5 text-xs dark:border-ink-700">
              {[
                { value: false, label: 'All' },
                { value: true, label: 'Unread' },
              ].map((option) => (
                <button
                  key={String(option.value)}
                  type="button"
                  onClick={() => setUnreadOnly(option.value)}
                  className={cn(
                    'rounded-md px-2.5 py-1 transition',
                    unreadOnly === option.value
                      ? 'bg-ink-900 text-white dark:bg-ink-100 dark:text-ink-900'
                      : 'text-ink-600 hover:bg-ink-100 dark:text-ink-300 dark:hover:bg-ink-800',
                  )}
                >
                  {option.label}
                </button>
              ))}
            </div>
            <Button size="sm" onClick={() => markAllRead.mutate()} disabled={count === 0} loading={markAllRead.isPending}>
              Mark all read
            </Button>
          </div>
        }
      />

      <div className="mt-6 rounded-card border border-ink-200 bg-white p-1.5 dark:border-ink-800 dark:bg-ink-950">
        {feed.isPending ? (
          <div className="flex justify-center py-12">
            <Spinner />
          </div>
        ) : feed.isError ? (
          <ErrorBanner error={feed.error} onRetry={() => void feed.refetch()} className="m-1.5" />
        ) : items.length === 0 ? (
          <p className="px-3 py-12 text-center text-sm text-ink-500">
            {unreadOnly ? 'Nothing unread.' : 'When someone assigns you an issue, mentions you or messages you, it shows up here.'}
          </p>
        ) : (
          <div className="divide-y divide-ink-100 dark:divide-ink-900">
            {items.map((notification) => (
              <NotificationRow
                key={notification.id}
                notification={notification}
                onOpen={() => {
                  if (!notification.read_at) markRead.mutate(notification.id)
                }}
                onToggleRead={() =>
                  notification.read_at ? markUnread.mutate(notification.id) : markRead.mutate(notification.id)
                }
              />
            ))}
          </div>
        )}
        {feed.hasNextPage ? (
          <div className="flex justify-center py-2">
            <Button size="sm" variant="ghost" onClick={() => void feed.fetchNextPage()} loading={feed.isFetchingNextPage}>
              Show older
            </Button>
          </div>
        ) : null}
      </div>

      <p className="mt-4 text-xs text-ink-500">
        Choose what you are told about in{' '}
        <Link to={workspace.path('/settings/notifications')} className="font-medium text-brand-700 hover:underline dark:text-brand-300">
          Settings → Notifications
        </Link>
        .
      </p>
    </div>
  )
}
